import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { CHART_RETURN_HOURS } from '@wodi/domain';
import type { ChartLocationView, RecordsSettingsView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Row,
  Section,
  Select,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import {
  chartKeys,
  createChartLocation,
  recordsSettingsQuery,
  saveRecordsSettings,
  updateChartLocation,
} from '../api/charts.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './records-page.tsx';
import styles from './records.module.css';

// Settings > Records (docs/plans/phase2-medical-records-charts.md §6): the chart return
// period and the records-room locations. Locations are switched off, never deleted; charts
// keep the history of where they were.

function useSave() {
  const queryClient = useQueryClient();
  return (view: RecordsSettingsView) => {
    queryClient.setQueryData(chartKeys.settings, view);
    void queryClient.invalidateQueries({ queryKey: chartKeys.all });
  };
}

function ReturnPeriod({ view }: { view: RecordsSettingsView }) {
  const save = useSave();
  const online = useOnline();
  const [hours, setHours] = useState(view.returnHours === null ? '' : String(view.returnHours));
  const value = Number(hours);
  const valid =
    hours === '' ||
    (Number.isInteger(value) && value >= CHART_RETURN_HOURS.min && value <= CHART_RETURN_HOURS.max);
  const next = hours === '' ? null : value;
  const update = useMutation({
    mutationFn: () => saveRecordsSettings({ returnHours: next }),
    onSuccess: (saved) => {
      save(saved);
      toasts.show({ title: 'Return period saved' });
    },
  });
  return (
    <Section
      title="Chart return period"
      description="How long a chart may be out before it is listed as overdue. Leave it empty to list none as overdue."
    >
      <form
        className={styles.actions}
        aria-label="Chart return period"
        onSubmit={(e) => {
          e.preventDefault();
          if (valid) update.mutate();
        }}
      >
        <TextField
          label="Return period"
          unit="hours"
          inputMode="numeric"
          width="sm"
          value={hours}
          onChange={setHours}
          {...(valid
            ? {}
            : {
                errorMessage: `Whole hours from ${String(CHART_RETURN_HOURS.min)} to ${String(CHART_RETURN_HOURS.max)}.`,
              })}
        />
        <Button
          type="submit"
          variant="primary"
          isDisabled={!online || !valid || next === view.returnHours}
          isPending={update.isPending}
          pendingLabel="Saving…"
        >
          Save return period
        </Button>
      </form>
      {update.error === null ? null : (
        <Notice tone="error">{errorText(update.error, 'The return period was not saved.')}</Notice>
      )}
    </Section>
  );
}

function LocationDialog({
  location,
  onClose,
}: {
  /** Null to add a new location. */
  location: ChartLocationView | null;
  onClose: () => void;
}) {
  const save = useSave();
  const [room, setRoom] = useState(location?.room ?? 'Records room');
  const [label, setLabel] = useState(location?.label ?? '');
  const [order, setOrder] = useState(String(location?.sortOrder ?? 0));
  const [status, setStatus] = useState<ChartLocationView['status']>(location?.status ?? 'active');
  const sortOrder = Number(order);
  const mutation = useMutation({
    mutationFn: () => {
      const body = { room: room.trim(), label: label.trim(), sortOrder, status };
      return location === null ? createChartLocation(body) : updateChartLocation(location.id, body);
    },
    onSuccess: (next) => {
      save(next);
      toasts.show({ title: location === null ? 'Location added' : 'Location saved' });
      onClose();
    },
  });
  const ready =
    room.trim() !== '' &&
    label.trim() !== '' &&
    Number.isInteger(sortOrder) &&
    sortOrder >= 0 &&
    sortOrder <= 9999;
  return (
    <Dialog
      title={location === null ? 'Add location' : `Location ${location.label}`}
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <TextField label="Room" value={room} onChange={setRoom} />
      <TextField
        label="Shelf or place"
        description="Like Shelf A3."
        value={label}
        onChange={setLabel}
      />
      <TextField
        label="Order in lists"
        inputMode="numeric"
        width="sm"
        value={order}
        onChange={setOrder}
      />
      {location === null ? null : (
        <Select
          label="State"
          options={[
            { id: 'active', label: 'In use' },
            { id: 'inactive', label: 'Not in use' },
          ]}
          value={status}
          onChange={(v) => {
            if (v === 'active' || v === 'inactive') setStatus(v);
          }}
        />
      )}
      {mutation.error === null ? null : (
        <Notice tone="error">{errorText(mutation.error, 'The location was not saved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!ready}
          isPending={mutation.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mutation.mutate();
          }}
        >
          {location === null ? 'Add location' : 'Save location'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function Locations({ view }: { view: RecordsSettingsView }) {
  const online = useOnline();
  const [editing, setEditing] = useState<ChartLocationView | 'new' | null>(null);
  return (
    <Section
      title="Records-room locations"
      description="Where charts are filed. A location not in use stays in the history of the charts filed there."
      actions={
        <Button
          isDisabled={!online}
          onPress={() => {
            setEditing('new');
          }}
        >
          Add location
        </Button>
      }
    >
      {view.locations.length === 0 ? (
        <p className={styles.meta}>No locations yet. Add one for each shelf or cabinet.</p>
      ) : (
        <TableScroll>
          <Table aria-label="Records-room locations" flat>
            <TableHeader>
              <Column isRowHeader>Shelf or place</Column>
              <Column>Room</Column>
              <Column>State</Column>
              <Column>Action</Column>
            </TableHeader>
            <TableBody items={view.locations}>
              {(l) => (
                <Row id={l.id}>
                  <Cell>{l.label}</Cell>
                  <Cell>{l.room}</Cell>
                  <Cell>
                    <Tag state={l.status === 'active' ? 'active' : 'neutral'}>
                      {l.status === 'active' ? 'In use' : 'Not in use'}
                    </Tag>
                  </Cell>
                  <Cell>
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      onPress={() => {
                        setEditing(l);
                      }}
                    >
                      Change
                    </Button>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      )}
      {editing === null ? null : (
        <LocationDialog
          location={editing === 'new' ? null : editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </Section>
  );
}

export function RecordsSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'records.settings.manage');
  const settings = useQuery({ ...recordsSettingsQuery, enabled: allowed });
  if (!allowed) {
    return (
      <SettingsPage title="Records">
        <Notice tone="info">You don&apos;t have permission to set up medical records.</Notice>
      </SettingsPage>
    );
  }
  return (
    <SettingsPage title="Records">
      {settings.isPending ? <LoadingBlock label="Loading the records settings" rows={3} /> : null}
      {settings.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void settings.refetch()}>Try again</Button>}
        >
          {errorText(settings.error, 'The settings could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {settings.isSuccess ? (
        <>
          <ReturnPeriod view={settings.data} />
          <Locations view={settings.data} />
        </>
      ) : null}
    </SettingsPage>
  );
}
